import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import type { AdArea } from '@/nova/features/ftux/catalog';
import { ftuxSignal } from '@/nova/features/ftux/signals';
import { adsNewTarget } from '@/nova/features/ftux/targets';
import { useMediaQuery } from '@/nova/hooks/useMediaQuery';
import { MOBILE } from '@/nova/theme/tokens';
import type { CreativeJob } from '@/store/slices/creativeJobs';

import { DotField } from '../../../customizer/components/DotField';
import { LumioMark } from '../LumioMark';
import { LibrarySkeletonCards } from './LibrarySkeletonCards';
import { openRowUnlessControl } from './row-open';
import * as S from './AdLibraryShell.styles';

/** The fields the shell reads from every ad entity (banner kit, mini-game). */
export interface AdLibraryShellItem {
  id: string;
  name: string;
  archived: boolean;
  job_id?: string;
}

/** A card's glanceable state: never generated, generated (a locked version exists), or a run in flight. */
export type AdCardState = 'draft' | 'ready' | 'generating';

/**
 * Props of {@link AdLibraryShell}. Every copy prop is ALREADY TRANSLATED by the caller (with its own translate call,
 * so the extractor harvests the key and any `{{n}}` is interpolated there) and is rendered verbatim.
 */
export interface AdLibraryShellProps<T extends AdLibraryShellItem> {
  /** Which library this is (set as `data-ad-kind` on the root). */
  kind: 'banner_kit' | 'playable' | 'html5_banner' | 'landing_page';
  /** Every entity of the brand, archived ones included (the shell splits them). */
  items: T[];
  /** The brand's FINAL list has landed (or failed to): until then the grid shows skeleton cards, never an empty grid
   *  that fills in. Stays true while the same brand's list is read again, so the cards stay put. */
  ready: boolean;
  /** Translated. The header, e.g. "Your image banners". */
  title: string;
  /** Translated. The one-line description under the header. */
  intro: string;
  /** Translated, count interpolated. The archived link and the archived sheet's title, e.g. "Archived image banners (3)". */
  archivedTitle: string;
  /** Translated. The archived sheet once it is empty. */
  archivedEmptyText: string;
  /** Translated. The grid with no live entity. */
  emptyText: string;
  /** Translated. What the skeleton cards stand for while the list loads, read out by assistive tech. */
  loadingText: string;
  /** Translated. The "create new" tile's label. */
  createLabel: string;
  /** Translated. The "create new" tile's accessible name, e.g. "Create a new image banner". */
  createAriaLabel: string;
  /** Translated. When set, the "create new" tile is disabled and shows this reason under its label (e.g. the brand
   *  has no promotion to advertise yet). */
  createDisabledText?: string;
  /** Translated. The name shown for an entity with none. */
  untitledName: string;
  /** Translated. The archive confirmation's title, e.g. "Archive this image banner?". */
  archiveConfirmTitle: string;
  /** Translated. The archive confirmation's explanation. */
  archiveConfirmBody: string;
  /** The card poster's content (an image or a placeholder), painted inside the fixed-ratio poster frame. */
  posterOf: (item: T) => React.ReactNode;
  /** The archived row's thumbnail url; none = an empty tile. */
  thumbOf?: (item: T) => string | undefined;
  /** Translated. The card's one-line meta, e.g. "17 sizes". */
  metaOf: (item: T) => string;
  stateOf: (item: T) => AdCardState;
  /** Overrides the poster's status chip (label translated by the caller), e.g. "Live" for a page made elsewhere.
   *  None = the chip {@link stateOf} implies. */
  statusOf?: (item: T) => { label: string; tone: 'ready' | 'draft' } | undefined;
  /** A view-only entity: its card opens on click but has no Edit / Download / Archive actions. */
  readOnlyOf?: (item: T) => boolean;
  /** Extra card content under the name / actions row (e.g. a stats row). */
  cardFooterOf?: (item: T) => React.ReactNode;
  /** The card footer's skeleton, shown in each skeleton card while the list loads (keeps the cards' height). */
  cardFooterSkeleton?: React.ReactNode;
  onCreate: () => void;
  onOpen: (item: T) => void;
  /** Download the entity's output. No handler = no download button. */
  onDownload?: (item: T) => void;
  /** Whether there is anything to download yet (default: yes). */
  canDownload?: (item: T) => boolean;
  /** A download of this entity is being prepared (the button is disabled meanwhile). */
  downloadBusyOf?: (item: T) => boolean;
  /** Archive (recoverable from the archived sheet). */
  remove: (id: string) => Promise<boolean>;
  restore: (id: string) => Promise<boolean>;
  /** Permanently delete an archived entity (armed by a second confirm in the archived sheet). */
  purge: (id: string) => Promise<boolean>;
  /** Live jobs by id: a generating card's progress bar reads its job's `completed / total`. */
  jobsById: Record<string, CreativeJob>;
}

/** The FTUX ad piece each library belongs to, by its area (landing pages have none). Inert outside an FTUX step. */
const FTUX_AD_AREA: Readonly<Record<AdLibraryShellProps<AdLibraryShellItem>['kind'], AdArea | null>> = {
  banner_kit: 'banner-kits',
  html5_banner: 'html5-banners',
  playable: 'playables',
  landing_page: null,
};

/** A stable per-entity seed for the generating dot-field, so simultaneously generating cards do not drift in sync. */
const seedFromId = (id: string): number => {
  let s = 0;
  for (let i = 0; i < id.length; i += 1) s = (s + id.charCodeAt(i) * (i + 1)) % 100000;
  return s;
};

/** A generating card's progress (0..100) from its job's unit counts; 0 until the job reports a total. Pure. */
const jobProgressPct = (job: CreativeJob | undefined): number => {
  const total = job?.total ?? 0;
  if (!job || total <= 0) return 0;
  return Math.max(0, Math.min(100, ((job.completed ?? 0) / total) * 100));
};

/**
 * The shared library of an ad kind (Image Banners, HTML5 Banners, Mini-games): a "create new" tile plus one card per live entity
 * (poster with a status chip or a generating overlay, name, meta, Edit / Download / Archive), an Archived sheet with
 * Restore and a two-step permanent delete, and an archive confirmation. Presentational: the caller owns the list,
 * the navigation and the copy. Same look as the Video Ads library.
 */
export const AdLibraryShell = <T extends AdLibraryShellItem>({
  kind,
  items,
  ready,
  title,
  intro,
  archivedTitle,
  archivedEmptyText,
  emptyText,
  loadingText,
  createLabel,
  createAriaLabel,
  createDisabledText,
  untitledName,
  archiveConfirmTitle,
  archiveConfirmBody,
  posterOf,
  thumbOf,
  metaOf,
  stateOf,
  statusOf,
  readOnlyOf,
  cardFooterOf,
  cardFooterSkeleton,
  onCreate,
  onOpen,
  onDownload,
  canDownload,
  downloadBusyOf,
  remove,
  restore,
  purge,
  jobsById,
}: AdLibraryShellProps<T>): React.ReactElement => {
  const { t } = useTranslation();
  // On a phone the cards are rows, and a tap anywhere on one opens it (its own buttons keep their actions).
  const rowsOpen = useMediaQuery(MOBILE);

  // The Archived sheet: archived entities are pulled out of the grid and reachable via the header link; each row
  // offers Restore or a two-step permanent delete (`confirmPurgeId` arms the destructive confirm per row).
  const [archivedOpen, setArchivedOpen] = React.useState(false);
  const [confirmPurgeId, setConfirmPurgeId] = React.useState<string | null>(null);
  const closeArchived = React.useCallback((): void => {
    setArchivedOpen(false);
    setConfirmPurgeId(null);
  }, []);
  const { closeHandle: archivedCloseHandle } = useSheetDismiss(closeArchived);

  // Archiving (the card trash button) is confirmed first, explaining it is recoverable, so it never reads as a
  // permanent delete.
  const [confirmArchiveId, setConfirmArchiveId] = React.useState<string | null>(null);
  const { closeHandle: archiveConfirmCloseHandle, dismiss: dismissArchiveConfirm } = useSheetDismiss(() =>
    setConfirmArchiveId(null),
  );

  const live = items.filter((item) => !item.archived);
  const archived = items.filter((item) => item.archived);
  const nameOf = (item: T): string => item.name || untitledName;
  // Where the FTUX's ad piece lights Create new: only a live tile, so a disabled one is never pointed at (the piece
  // then ends where it stands, its card waiting on Spotlight).
  const ftuxArea = FTUX_AD_AREA[kind];

  return (
    <div data-ad-kind={kind}>
      <S.Intro>
        <S.IntroHead>
          <S.Title raw text={title} />
          {ready && archived.length > 0 ? (
            <S.ArchivedLink type="button" onClick={() => setArchivedOpen(true)}>
              <Icon name="trash" size={13} />
              <S.ArchivedLinkText raw text={archivedTitle} />
            </S.ArchivedLink>
          ) : null}
        </S.IntroHead>
        <S.Body raw text={intro} />
      </S.Intro>

      <S.SavedGrid>
        <S.NewTile
          type="button"
          onClick={() => {
            if (createDisabledText) return; // self-gate: disabled styling alone is not a guard
            if (ftuxArea) ftuxSignal('ads-new-pressed'); // FTUX: the ad piece hands the library over (no-op outside it)
            onCreate();
          }}
          aria-label={createDisabledText ? `${createAriaLabel}. ${createDisabledText}` : createAriaLabel}
          disabled={Boolean(createDisabledText)}
          data-ftux={ftuxArea && !createDisabledText ? adsNewTarget(ftuxArea) : undefined}
        >
          <Icon name="plus-circle" size={26} />
          <S.NewTileText raw text={createLabel} />
          {createDisabledText ? <S.NewTileNote raw text={createDisabledText} /> : null}
        </S.NewTile>
        {ready ? null : <LibrarySkeletonCards label={loadingText} footer={cardFooterSkeleton} />}
        {(ready ? live : []).map((item) => {
          const state = stateOf(item);
          const generating = state === 'generating';
          const job = item.job_id ? jobsById[item.job_id] : undefined;
          const downloadable = canDownload ? canDownload(item) : true;
          const downloadBusy = downloadBusyOf ? downloadBusyOf(item) : false;
          const footer = cardFooterOf ? cardFooterOf(item) : null;
          const status = statusOf ? statusOf(item) : undefined;
          const readOnly = readOnlyOf ? readOnlyOf(item) : false;
          return (
            <S.SavedCard
              key={item.id}
              $reveal
              $opens={rowsOpen}
              onClick={rowsOpen ? (event) => openRowUnlessControl(event, () => onOpen(item)) : undefined}
            >
              <S.Poster type="button" onClick={() => onOpen(item)} aria-label={t('Open {{name}}', { name: nameOf(item) })}>
                {generating ? null : status ? (
                  <S.StatusPill>
                    <S.StatusDot $tone={status.tone} aria-hidden="true" />
                    <S.StatusPillText raw text={status.label} />
                  </S.StatusPill>
                ) : (
                  <S.StatusPill>
                    <S.StatusDot $tone={state === 'ready' ? 'ready' : 'draft'} aria-hidden="true" />
                    {state === 'ready' ? <S.StatusPillText text="Ready" /> : <S.StatusPillText text="Draft" />}
                  </S.StatusPill>
                )}
                {posterOf(item)}
                {generating ? (
                  <S.BusyOverlay aria-live="polite">
                    <DotField seed={seedFromId(item.id)} />
                    <S.BusyInner>
                      {/* Lumio brand mark for this AI run (never an approximated sparkle/star). */}
                      <LumioMark size={22} />
                      <S.BusyText text="Generating…" />
                    </S.BusyInner>
                    {job ? (
                      <S.CardProgressTrack
                        role="progressbar"
                        aria-label={t('Generating…')}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(jobProgressPct(job))}
                      >
                        <S.CardProgressFill $pct={jobProgressPct(job)} />
                      </S.CardProgressTrack>
                    ) : null}
                  </S.BusyOverlay>
                ) : null}
              </S.Poster>
              <S.SavedBody>
                <S.SavedName raw text={nameOf(item)} />
                <S.SavedFoot>
                  <S.SavedMeta raw text={metaOf(item)} />
                  {readOnly ? null : (
                    <S.SavedActions>
                      <S.ActionBtn type="button" onClick={() => onOpen(item)} aria-label={t('Edit')}>
                        <Icon name="edit" size={14} />
                      </S.ActionBtn>
                      {onDownload ? (
                        <S.ActionBtn
                          type="button"
                          onClick={() => {
                            if (!downloadable || downloadBusy) return; // self-gate: disabled styling alone is not a guard
                            onDownload(item);
                          }}
                          aria-label={t('Download')}
                          aria-busy={downloadBusy || undefined}
                          disabled={!downloadable || downloadBusy}
                        >
                          <Icon name="download" size={14} />
                        </S.ActionBtn>
                      ) : null}
                      <S.ActionBtn type="button" onClick={() => setConfirmArchiveId(item.id)} aria-label={t('Archive')}>
                        <Icon name="trash" size={14} />
                      </S.ActionBtn>
                    </S.SavedActions>
                  )}
                </S.SavedFoot>
                {footer}
              </S.SavedBody>
            </S.SavedCard>
          );
        })}
      </S.SavedGrid>
      {ready && live.length === 0 ? <S.EmptyNote raw text={emptyText} /> : null}

      {confirmArchiveId ? (
        <Sheet
          title={archiveConfirmTitle}
          onClose={() => setConfirmArchiveId(null)}
          closeHandle={archiveConfirmCloseHandle}
          maxWidth="460px"
          footer={
            <S.ConfirmFoot>
              <Button variant="text" label={t('Cancel')} onClick={dismissArchiveConfirm} />
              <Button
                variant="primary"
                label={t('Archive')}
                onClick={() => {
                  const id = confirmArchiveId;
                  dismissArchiveConfirm();
                  void remove(id);
                }}
              />
            </S.ConfirmFoot>
          }
        >
          <S.ConfirmBody raw text={archiveConfirmBody} />
        </Sheet>
      ) : null}

      {archivedOpen ? (
        <Sheet title={archivedTitle} onClose={closeArchived} closeHandle={archivedCloseHandle} maxWidth="560px">
          <S.ArchivedList>
            {archived.length === 0 ? (
              <S.EmptyNote raw text={archivedEmptyText} />
            ) : (
              archived.map((item) => {
                const thumb = thumbOf ? thumbOf(item) : undefined;
                return (
                  <S.ArchivedRow key={item.id}>
                    <S.ArchivedThumb>{thumb ? <S.ArchivedThumbImg src={thumb} alt="" /> : null}</S.ArchivedThumb>
                    <S.ArchivedInfo>
                      <S.ArchivedName raw text={nameOf(item)} />
                      <S.SavedMeta raw text={metaOf(item)} />
                    </S.ArchivedInfo>
                    {confirmPurgeId === item.id ? (
                      <S.ArchivedActions>
                        <S.ArchivedConfirmText text="Delete forever?" />
                        <Button variant="text" label={t('Cancel')} onClick={() => setConfirmPurgeId(null)} />
                        <S.DangerBtn
                          type="button"
                          onClick={() => {
                            setConfirmPurgeId(null);
                            void purge(item.id);
                          }}
                        >
                          <Icon name="trash" size={14} />
                          <S.DangerBtnText text="Delete" />
                        </S.DangerBtn>
                      </S.ArchivedActions>
                    ) : (
                      <S.ArchivedActions>
                        <Button variant="outline" label={t('Restore')} onClick={() => void restore(item.id)} />
                        <S.ArchivedDeleteBtn
                          type="button"
                          onClick={() => setConfirmPurgeId(item.id)}
                          aria-label={t('Delete permanently')}
                        >
                          <Icon name="trash" size={15} />
                        </S.ArchivedDeleteBtn>
                      </S.ArchivedActions>
                    )}
                  </S.ArchivedRow>
                );
              })
            )}
          </S.ArchivedList>
        </Sheet>
      ) : null}
    </div>
  );
};

export default AdLibraryShell;
